import React, { FC } from "react";

import { KpkApiPublicShareStatus } from "@keepeek/api-client";
import { SentryCallback, SnackbarCallback, useBasketShare } from "@keepeek/commons";
import {
  KModal,
  KModalAction,
  KModalHeader,
  SingleSimpleAccordion,
  withSuspense,
} from "@keepeek/refront-components";
import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { KIcon } from "../../../components/common/KIcon";
import { CONFIGURATION_SECTION_PATH } from "../../../components/layout/adminMenu";
import { ShareManager as ShareManagerConfig } from "../../../models/configuration/components/shareManager";
import { useConfiguration } from "../../../providers/config/hooks";
import { configSectionComponentBasketSelector } from "../../../providers/config/selectors";
import PublicShareAccessCode from "./PublicShareAccessCode";
import PublicShareActivate from "./PublicShareActivate";
import PublicShareAttachmentsVisible from "./PublicShareAttachmentsVisible";
import PublicShareDeactivationDate from "./PublicShareDeactivationDate";
import PublicShareEntryPoint from "./PublicShareEntryPoint";
import PublicShareExportDefinitions from "./PublicShareExportDefinitions";

const PREFIX = "BasketShare";

const classes = {
  content: `${PREFIX}-content`,
  iconAction: `${PREFIX}-iconAction`,
  iconActionAlt: `${PREFIX}-iconActionAlt`,
};

const StyledKModal = styled(KModal)(({ theme }) => ({
  [`& .${classes.content}`]: {
    position: "relative",
    [`& .PublicShareEntryPoint-shareLink`]: {
      paddingTop: theme.spacing(1),
    },
    [`& .MuiAccordionSummary-root`]: {
      minHeight: "none",
      marginTop: theme.spacing(2),
    },
    [`& .MuiAccordion-root`]: {
      color: theme.palette.primary.main,
    },
    "& h1": {
      fontSize: "20px",
      color: theme.palette.primary.main,
    },
  },

  [`& .${classes.iconAction}`]: {
    marginRight: theme.spacing(2),
  },

  [`& .${classes.iconActionAlt}`]: {
    marginRight: theme.spacing(2),
    color: theme.palette.primary.main,
  },
}));

export type CommonBasketShareProps = {
  basketId: number;
  basketTitle?: string;
  sentryCallback?: SentryCallback;
  snackbarCallback?: SnackbarCallback;
};

const BasketShare: FC<CommonBasketShareProps> = function ({
  basketId,
  basketTitle,
  sentryCallback,
  snackbarCallback,
}) {
  const { t } = useTranslation();
  const shareManagerConfig = useConfiguration<ShareManagerConfig>(
    CONFIGURATION_SECTION_PATH.COMPONENTS_SHARE_MANAGER,
  );
  const basketPublicShareConfig = shareManagerConfig?.basketPublicShare;
  const emptyAdvancedSettings =
    basketPublicShareConfig?.disableAccessCode &&
    basketPublicShareConfig?.disableAttachmentsVisible &&
    basketPublicShareConfig?.disableDeactivationDate &&
    basketPublicShareConfig?.disableExportDefinition;
  const { displayBackofficeSharedBasket } = useRecoilValue(
    configSectionComponentBasketSelector,
  ) ?? {
    displayBackofficeSharedBasket: undefined,
  };
  const { open, close, publish, unPublish, selectedStatus } = useBasketShare(
    displayBackofficeSharedBasket ?? false,
    basketId,
    sentryCallback,
    snackbarCallback,
  );
  if (!open) {
    return null;
  }
  return (
    <StyledKModal
      className="BasketShare"
      open={open}
      onClose={close}
      header={
        <KModalHeader
          onClose={close}
          title={t("basket.share.header.label", { basketTitle: basketTitle }) as string}
        />
      }
    >
      <Box className={classes.content} data-testid="BasketSharePopup">
        <PublicShareActivate
          basketId={basketId}
          basketTitle={basketTitle}
          sentryCallback={sentryCallback}
          snackbarCallback={snackbarCallback}
        />
        <PublicShareEntryPoint
          basketId={basketId}
          basketTitle={basketTitle}
          sentryCallback={sentryCallback}
          snackbarCallback={snackbarCallback}
        />
        {selectedStatus === KpkApiPublicShareStatus.Activated && (
          <KModalAction
            onClick={unPublish}
            alt
            sx={{
              textAlign: "left",
              paddingTop: "8px",
              "& button": {
                borderColor: "rgba(14, 38, 49, 0.3) !important",
              },
            }}
          >
            <KIcon className={`${classes.iconActionAlt} fa-solid fa-circle-xmark fa-lg`} />
            {t("basket.share.action.unpublish.label")}
          </KModalAction>
        )}
        {!emptyAdvancedSettings && (
          <SingleSimpleAccordion titleComponent={t("basket.share.advanced.settings")}>
            {!basketPublicShareConfig?.disableExportDefinition && (
              <PublicShareExportDefinitions
                basketId={basketId}
                sentryCallback={sentryCallback}
                snackbarCallback={snackbarCallback}
              />
            )}
            {displayBackofficeSharedBasket !== undefined &&
              !displayBackofficeSharedBasket &&
              !basketPublicShareConfig?.disableAttachmentsVisible && (
                <PublicShareAttachmentsVisible
                  basketId={basketId}
                  sentryCallback={sentryCallback}
                  snackbarCallback={snackbarCallback}
                />
              )}
            {!basketPublicShareConfig?.disableDeactivationDate && (
              <PublicShareDeactivationDate
                basketId={basketId}
                sentryCallback={sentryCallback}
                snackbarCallback={snackbarCallback}
              />
            )}
            {!basketPublicShareConfig?.disableAccessCode && (
              <PublicShareAccessCode
                basketId={basketId}
                sentryCallback={sentryCallback}
                snackbarCallback={snackbarCallback}
              />
            )}
            <KModalAction
              onClick={() => publish({ closePopup: true })}
              sx={{ textAlign: "left", paddingBottom: "8px" }}
            >
              {selectedStatus === KpkApiPublicShareStatus.Activated ? (
                <KIcon className={`${classes.iconAction} ${"fa-light fa-circle-check fa-lg"}`} />
              ) : (
                <KIcon className={`${classes.iconAction} ${" fa-regular fa-arrow-up-to-line"}`} />
              )}
              {selectedStatus === KpkApiPublicShareStatus.Activated
                ? t("basket.share.action.update.label")
                : t("basket.share.action.publish.label")}
            </KModalAction>
          </SingleSimpleAccordion>
        )}
      </Box>
    </StyledKModal>
  );
};

export default withSuspense(BasketShare);
